import type { BidInput, PackageDef, PricingConfig, SurfaceDef } from '@lbhl/shared';

export interface ResolvedContext {
  /**
   * Every package, in config sort order. The first is the base tier: the one
   * whose surfaces every other package also includes.
   */
  packagesInOrder: PackageDef[];
  surfacesByCode: Map<string, SurfaceDef>;
}

/**
 * Validates a bid input against a pricing config and resolves the bits the
 * engine needs. Collects every issue rather than failing on the first, so the
 * UI can show all of them at once.
 */
export function resolveAndValidate(
  config: PricingConfig,
  input: BidInput,
): { context: ResolvedContext; issues: string[] } {
  const issues: string[] = [];

  const packagesInOrder = [...config.packages].sort((a, b) => a.sort - b.sort);
  const surfacesByCode = new Map(config.surfaces.map((s) => [s.code, s]));
  const packageCodes = new Set(packagesInOrder.map((p) => p.code));
  const skus = new Set(config.inventoryItems.map((i) => i.sku));

  if (packagesInOrder.length === 0) {
    issues.push('Pricing config defines no packages.');
  }

  if (!config.colorOptions.some((c) => c.code === input.colorCode)) {
    issues.push(`Unknown light colour "${input.colorCode}".`);
  }
  if (!config.clipColorOptions.some((c) => c.code === input.clipColorCode)) {
    issues.push(`Unknown clip colour "${input.clipColorCode}".`);
  }

  if (!Number.isFinite(input.travelMiles) || input.travelMiles < 0) {
    issues.push('Travel miles must be zero or more.');
  }

  const seen = new Set<string>();
  for (const m of input.measurements) {
    if (seen.has(m.surfaceCode)) {
      issues.push(`Surface "${m.surfaceCode}" appears more than once.`);
    }
    seen.add(m.surfaceCode);

    const surface = surfacesByCode.get(m.surfaceCode);
    if (!surface) {
      issues.push(`Unknown surface "${m.surfaceCode}".`);
      continue;
    }

    // A surface no package measures can never be priced, so it would be silently
    // free. Every surface has to belong to at least one tier.
    if (!packagesInOrder.some((p) => p.surfaces.includes(surface.code))) {
      issues.push(`Surface "${surface.name}" is not part of any package.`);
    }

    for (const [label, value] of [
      ['linear feet', m.linearFeet],
      ['unit count', m.unitCount],
      ['strand count', m.strandCount],
    ] as const) {
      if (value !== undefined && (!Number.isFinite(value) || value < 0)) {
        issues.push(`${surface.name}: ${label} must be zero or more.`);
      }
    }

    issues.push(...segmentIssues(m.segments, surface, m));

    if (surface.isAccent) {
      if (m.linearFeet !== undefined && m.linearFeet > 0) {
        issues.push(
          `${surface.name} is accent lighting, priced per strand, not per foot.`,
        );
      }
    } else if (m.strandCount !== undefined && m.strandCount > 0) {
      issues.push(
        `${surface.name} is a C9 run, priced per foot, not per strand.`,
      );
    }
  }

  // Every package includes the base tier's surfaces, so a bid with nothing
  // measured there would quote the base package at zero and send that to a
  // customer. That is a mis-entry, not a free job.
  const basePkg = packagesInOrder[0];
  if (basePkg) {
    const baseFeet = input.measurements
      .filter((m) => {
        const surface = surfacesByCode.get(m.surfaceCode);
        return surface && !surface.isAccent && basePkg.surfaces.includes(surface.code);
      })
      .reduce((sum, m) => sum + (m.linearFeet ?? 0), 0);

    if (baseFeet <= 0) {
      issues.push(
        `${basePkg.name} has no measured footage. Every package includes it, so the bid would quote ${basePkg.name} at zero.`,
      );
    }
  }

  for (const [i, adjustment] of (input.adjustments ?? []).entries()) {
    const which = adjustment.label || `${i + 1}`;
    if (!packageCodes.has(adjustment.packageCode)) {
      issues.push(`Adjustment "${which}" names unknown package "${adjustment.packageCode}".`);
    }
    // A required reason is the guard against silent, unexplainable overrides.
    if (!adjustment.label || adjustment.label.trim() === '') {
      issues.push(`Adjustment ${i + 1} needs a reason.`);
    }
    if (!Number.isInteger(adjustment.amountCents)) {
      issues.push(`Adjustment "${which}" must be a whole number of cents.`);
    }
  }

  for (const [i, count] of (input.engineering ?? []).entries()) {
    if (!packageCodes.has(count.packageCode)) {
      issues.push(
        `Engineering entry ${i + 1} names unknown package "${count.packageCode}".`,
      );
    }
    if (!skus.has(count.sku)) {
      issues.push(`Engineering entry ${i + 1} names unknown item "${count.sku}".`);
    }
    if (!Number.isFinite(count.qty) || count.qty < 0) {
      issues.push(`Engineering count for "${count.sku}" must be zero or more.`);
    }
  }

  for (const pkg of packagesInOrder) {
    const rate = pkg.ratePerFootOverrideCents ?? config.ratePerFootCents;
    if (!Number.isInteger(rate) || rate < 0) {
      issues.push(
        `${pkg.name}: per-foot rate must be a non-negative whole number of cents.`,
      );
    }
  }

  return { context: { packagesInOrder, surfacesByCode }, issues };
}

/**
 * Segments are a bidder's scratch work and the sum is what gets charged, so the
 * two disagreeing means the form computed the total wrongly. That must never
 * reach a customer, so it is an error rather than a warning.
 */
function segmentIssues(
  segments: number[] | undefined,
  surface: SurfaceDef,
  m: { linearFeet?: number | undefined; strandCount?: number | undefined },
): string[] {
  if (segments === undefined) return [];

  const issues: string[] = [];
  if (segments.some((s) => !Number.isFinite(s) || s < 0)) {
    issues.push(`${surface.name}: every entry must be zero or more.`);
    return issues;
  }

  const sum = segments.reduce((a, b) => a + b, 0);
  const priced = (surface.isAccent ? m.strandCount : m.linearFeet) ?? 0;

  // Two decimals because feet are numeric(10,2); it also absorbs the float drift
  // a sum of decimal entries picks up.
  if (Number(sum.toFixed(2)) !== Number(priced.toFixed(2))) {
    issues.push(
      `${surface.name}: entries add up to ${Number(sum.toFixed(2))} but ${Number(priced.toFixed(2))} was priced.`,
    );
  }

  return issues;
}
